import Title from '@cohort/components-xps/components/Title';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import CenteredContent from '@cohort/wallet/components/layout/CenteredContent';
import NftTransferringModal from '@cohort/wallet/components/modals/transfer/NftTransferringModal';
import TitleWithLeadingIcon from '@cohort/wallet/components/TitleWithLeadingIcon';
import {ownershipsKeys} from '@cohort/wallet/hooks/api/Ownerships';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import {OwnershipsApi} from '@cohort/wallet/lib/Endpoints';
import MerchantInfos from '@cohort/wallet/pages/space/MerchantInfos';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import {Check, X} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams, useSearchParams} from 'react-router-dom';
import {match} from 'ts-pattern';

type TransferState = 'not_started' | 'success' | 'error';

const DigitalAssetTransferRequestPage: React.FC = () => {
  const [transferState, setTransferState] = useState<TransferState>('not_started');
  const [successText, setSuccessText] = useState({
    title: '',
    subtitle: '',
  });
  const {accentColor} = useThemeContext();
  const confirmTransfer = useApi(OwnershipsApi.confirmTransfer);
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const {t} = useTranslation('pages', {keyPrefix: 'space.digitalAssetTransferRequestPage'});
  const validationCode = searchParams.get('validationCode');
  const {ownershipId} = useParams();
  const user = useUserStore(store => store.user);
  const [transferringOwnership, setTransferringOwnership] = useState<OwnershipWDto>();

  const {mutate: triggerOwnershipTransfer, isLoading: isTransferLoading} = useCohortMutation({
    mutationFn: async () => confirmTransfer(ownershipId, validationCode),
    onSuccess: ({ownership, recipientEmail}) => {
      setTransferState('success');
      if (recipientEmail !== undefined) {
        setSuccessText({
          title: t('emailSuccessTitle'),
          subtitle: t('emailSuccessSubtitle', {email: recipientEmail}),
        });
      } else {
        setSuccessText({
          title: t('polygonSuccessTitle'),
          subtitle: t('polygonSuccessSubtitle'),
        });
        if (user !== null) {
          setTransferringOwnership(ownership);
        }
      }
      queryClient.invalidateQueries(ownershipsKeys.getByOwnershipId(ownership.id));
    },
    onError: () => setTransferState('error'),
  });

  const content = match(transferState)
    .with('not_started', () => (
      <Fragment>
        <Title content={t('pageTitle')} />
        <PrimaryButton
          className="w-full md:w-1/2"
          text={t('transferButton')}
          onClick={() => triggerOwnershipTransfer()}
          isLoading={isTransferLoading}
          tracking={{
            namespace: 'digitalAssets.transferRequest.confirm',
            metadata: {
              ownershipId,
              validationCode,
            },
          }}
        />
      </Fragment>
    ))
    .with('error', () => (
      <TitleWithLeadingIcon
        title={t('errorTitle')}
        subtitle={t('errorSubtitle')}
        icon={<X className="h-11 w-11" style={{color: accentColor}} />}
      />
    ))
    .with('success', () => (
      <TitleWithLeadingIcon
        title={successText.title}
        subtitle={successText.subtitle}
        icon={<Check className="h-11 w-11" style={{color: accentColor}} />}
      />
    ))
    .exhaustive();

  return (
    <SpacePage
      title={t('pageTitle')}
      tracking={{
        namespace: 'digitalAssets.transferRequest',
        metadata: {
          ownershipId,
          validationCode,
        },
      }}
      leftPanel={<MerchantInfos />}
    >
      <CenteredContent className="space-y-4">{content}</CenteredContent>
      {transferringOwnership && <NftTransferringModal ownership={transferringOwnership} />}
    </SpacePage>
  );
};

export default DigitalAssetTransferRequestPage;
